import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchPlatformOverview,
  listUserCredits,
  PlatformOverview,
  UserCreditRow,
  allocateCredits,
  exportUserAllocations,
  exportUserUsedCredits
} from '../../services/CreditsService';
import { deleteUser } from '../../services/UserService';

const BillingFinancialManagement = () => {
  const [selectedPlan, setSelectedPlan] = useState('basic');
  const navigate = useNavigate();
  const [overview, setOverview] = useState<PlatformOverview | null>(null);
  const [distribution, setDistribution] = useState<UserCreditRow[]>([]);
  const [editingUser, setEditingUser] = useState<UserCreditRow | null>(null);
  const [newAllocated, setNewAllocated] = useState<number>(0);
  const [saving, setSaving] = useState<boolean>(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const pageSize = 10;
  const [exportUserId, setExportUserId] = useState<string>('');
  const [exportStartDate, setExportStartDate] = useState<string>('');
  const [exportEndDate, setExportEndDate] = useState<string>('');
  const [downloading, setDownloading] = useState<boolean>(false);
  const [usedUserId, setUsedUserId] = useState<string>('');
  const [usedStartDate, setUsedStartDate] = useState<string>('');
  const [usedEndDate, setUsedEndDate] = useState<string>('');
  const [downloadingUsed, setDownloadingUsed] = useState<boolean>(false);

  useEffect(() => {
    const load = async () => {
      try {
        const ov = await fetchPlatformOverview();
        setOverview(ov);
        const rows = await listUserCredits();
        setDistribution(rows);
        if (rows.length > 0) {
          setExportUserId(rows[0].userId);
          setUsedUserId(rows[0].userId);
        }
      } catch (e) {
        console.error(e);
      }
    };
    load();
  }, []);

  const refreshData = async () => {
    try {
      const [ov, rows] = await Promise.all([fetchPlatformOverview(), listUserCredits()]);
      setOverview(ov);
      setDistribution(rows);
      if (rows.length > 0) {
        setExportUserId((prev) => prev || rows[0].userId);
        setUsedUserId((prev) => prev || rows[0].userId);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const openEdit = (row: UserCreditRow) => {
    setEditingUser(row);
    setNewAllocated(row.allocated || 0);
  };

  const handleSaveEdit = async () => {
    if (!editingUser) return;
    const currentAllocated = editingUser.allocated || 0;
    const delta = Math.round(newAllocated) - Math.round(currentAllocated);
    if (delta === 0) {
      setEditingUser(null);
      return;
    }
    if (delta < 0) {
      setMessage({ type: 'error', text: 'Reducing allocated credits is not supported here.' });
      setTimeout(() => setMessage(null), 4000);
      return;
    }
    try {
      setSaving(true);
      const res = await allocateCredits({ userId: editingUser.userId, amount: delta });
      setOverview(res.overview);
      await refreshData();
      setMessage({ type: 'success', text: 'Credits updated successfully.' });
      setTimeout(() => setMessage(null), 3000);
      setEditingUser(null);
    } catch (e: any) {
      console.error(e);
      setMessage({ type: 'error', text: e?.message || 'Failed to update credits' });
      setTimeout(() => setMessage(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (row: UserCreditRow) => {
    const confirmed = window.confirm(`Delete user "${row.client}"? This action cannot be undone.`);
    if (!confirmed) return;
    try {
      await deleteUser(row.userId);
      setDistribution((prev) => prev.filter((r) => r.userId !== row.userId));
      setMessage({ type: 'success', text: 'User deleted.' });
      setTimeout(() => setMessage(null), 3000);
    } catch (e: any) {
      console.error(e);
      const text = e?.message || 'Failed to delete user';
      setMessage({ type: 'error', text });
      setTimeout(() => setMessage(null), 5000);
    }
  };

  // Reset to first page whenever the distribution list changes
  useEffect(() => {
    setCurrentPage(1);
  }, [distribution.length]);

  const handleDownloadAllocations = async () => {
    try {
      if (!exportUserId) {
        alert('Please select a user to export.');
        return;
      }
      if (!exportStartDate || !exportEndDate) {
        alert('Please select both a start date and an end date.');
        return;
      }
      if (exportStartDate > exportEndDate) {
        alert('Start date cannot be after end date.');
        return;
      }
      setDownloading(true);
      const blob = await exportUserAllocations({
        userId: exportUserId,
        startDate: exportStartDate,
        endDate: exportEndDate
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const selectedUser = distribution.find((u) => u.userId === exportUserId);
      const safeName = selectedUser?.client?.replace(/\s+/g, '_') || 'user';
      a.href = url;
      a.download = `credit_allocations_${safeName}_${exportStartDate}_${exportEndDate}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      alert(e?.message || 'Failed to download allocations.');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadUsedCredits = async () => {
    try {
      if (!usedUserId) {
        alert('Please select a user to export credits used.');
        return;
      }
      if (!usedStartDate || !usedEndDate) {
        alert('Please select both a start date and an end date for credits used.');
        return;
      }
      if (usedStartDate > usedEndDate) {
        alert('Start date cannot be after end date.');
        return;
      }
      setDownloadingUsed(true);
      const blob = await exportUserUsedCredits({
        userId: usedUserId,
        startDate: usedStartDate,
        endDate: usedEndDate
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const selectedUser = distribution.find((u) => u.userId === usedUserId);
      const safeName = selectedUser?.client?.replace(/\s+/g, '_') || 'user';
      a.href = url;
      a.download = `credit_usage_${safeName}_${usedStartDate}_${usedEndDate}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      alert(e?.message || 'Failed to download credits used.');
    } finally {
      setDownloadingUsed(false);
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-medium mb-6">Billing and Financial Management</h1>

      {/* Section 1: Manage Subscriptions and Pricing */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-medium">Subscription Management</h2>
          <div className="flex space-x-3">
            <button 
              onClick={() => navigate('/superadmin/subscribers')}
              className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#FF5722]/90 transition-colors"
            >
              Manage Subscribers
            </button>
            <button 
              onClick={() => navigate('/superadmin/billing/create-package')}
              className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#FF5722]/90 transition-colors"
            >
              Create New Plan
            </button>
          </div>
        </div>

        {/* Subscription Plans */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {[
            { name: 'Basic', price: '$29', users: '1-10', credits: '1,000' },
            { name: 'Professional', price: '$99', users: '10-50', credits: '5,000' },
            { name: 'Enterprise', price: '$299', users: 'Unlimited', credits: '20,000' }
          ].map((plan) => (
            <div 
              key={plan.name}
              className={`border rounded-lg p-6 cursor-pointer transition-all ${
                selectedPlan === plan.name.toLowerCase() 
                  ? 'border-[#FF5722] bg-[#FF5722]/5' 
                  : 'border-gray-200 hover:border-[#FF5722]/50'
              }`}
              onClick={() => setSelectedPlan(plan.name.toLowerCase())}
            >
              <h3 className="text-xl font-medium mb-2">{plan.name}</h3>
              <div className="text-3xl font-bold text-[#FF5722] mb-4">{plan.price}<span className="text-sm text-gray-600">/month</span></div>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Up to {plan.users} users</li>
                <li>• {plan.credits} SMS credits</li>
                <li>• 24/7 Support</li>
                <li>• Analytics Dashboard</li>
              </ul>
            </div>
          ))}
        </div>

        {/* Current Plan Stats */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <h3 className="font-medium mb-1">Active Subscriptions</h3>
              <div className="text-2xl font-bold text-[#FF5722]">1,234</div>
            </div>
            <div>
              <h3 className="font-medium mb-1">Monthly Revenue</h3>
              <div className="text-2xl font-bold text-[#FF5722]">$45,678</div>
            </div>
            <div>
              <h3 className="font-medium mb-1">Churn Rate</h3>
              <div className="text-2xl font-bold text-[#FF5722]">2.4%</div>
            </div>
            <div>
              <h3 className="font-medium mb-1">Avg. Customer Value</h3>
              <div className="text-2xl font-bold text-[#FF5722]">$89</div>
            </div>
          </div>
        </div>
      </div>

      {/* Section 2: Handle Payments and Invoices */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-medium">Payment Management</h2>
          <button 
            onClick={() => navigate('/billing/create-invoice')}
            className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#FF5722]/90 transition-colors"
          >
            Create Invoice
          </button>
        </div>

        {/* Payment Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Total Revenue</h3>
            <div className="text-2xl font-bold text-green-600">$123,456</div>
            <p className="text-sm text-gray-600">This month</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Pending Payments</h3>
            <div className="text-2xl font-bold text-yellow-600">$4,567</div>
            <p className="text-sm text-gray-600">12 invoices</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Overdue</h3>
            <div className="text-2xl font-bold text-red-600">$1,234</div>
            <p className="text-sm text-gray-600">5 invoices</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Paid</h3>
            <div className="text-2xl font-bold text-blue-600">$117,655</div>
            <p className="text-sm text-gray-600">89 invoices</p>
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {[
                { id: 'INV-001', customer: 'Acme Corp', amount: '$299', status: 'Paid', date: '2024-03-15' },
                { id: 'INV-002', customer: 'Tech Solutions', amount: '$99', status: 'Pending', date: '2024-03-14' },
                { id: 'INV-003', customer: 'Global Services', amount: '$599', status: 'Overdue', date: '2024-03-10' }
              ].map((invoice) => (
                <tr key={invoice.id}>
                  <td className="px-6 py-4 whitespace-nowrap">{invoice.id}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{invoice.customer}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{invoice.amount}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      invoice.status === 'Paid' ? 'bg-green-100 text-green-800' :
                      invoice.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {invoice.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">{invoice.date}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Section 3: Credit Allocation */}
      <div className="bg-white rounded-lg shadow-md p-6">
        {message ? (
          <div className={`mb-4 rounded-md p-3 border ${message.type === 'success' ? 'border-green-200 bg-green-50 text-green-800' : 'border-red-200 bg-red-50 text-red-800'}`}>
            {message.text}
          </div>
        ) : null}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-medium">Credit Management</h2>
          <div className="space-x-3">
            <button 
              onClick={() => navigate('/superadmin/billing/allocate-credits')}
              className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#FF5722]/90 transition-colors"
            >
              Allocate Credits
            </button>
            <button 
              onClick={() => navigate('/superadmin/billing/add-credits')}
              className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#FF5722]/90 transition-colors"
            >
              Add Credits
            </button>
          </div>
        </div>

        {/* Credit Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Total Credits</h3>
            <div className="text-2xl font-bold text-[#FF5722]">{(overview?.total || 0).toLocaleString()}</div>
            <p className="text-sm text-gray-600">Platform total</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Used Credits</h3>
            <div className="text-2xl font-bold text-[#FF5722]">{((overview?.allocated || 0) - (overview?.available || 0)).toLocaleString()}</div>
            <p className="text-sm text-gray-600">Estimated</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Reserved</h3>
            <div className="text-2xl font-bold text-[#FF5722]">{(overview?.allocated || 0).toLocaleString()}</div>
            <p className="text-sm text-gray-600">For campaigns</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Expiring</h3>
            <div className="text-2xl font-bold text-[#FF5722]">{(overview?.available || 0).toLocaleString()}</div>
            <p className="text-sm text-gray-600">Next 30 days</p>
          </div>
        </div>

        {/* Credit Allocation Table */}
        <div className="border rounded-lg p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
            <h3 className="font-medium">Credit Distribution</h3>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <select
                className="border border-gray-300 rounded-md px-2 py-1"
                value={exportUserId}
                onChange={(e) => setExportUserId(e.target.value)}
              >
                <option value="">Select user</option>
                {distribution.map((u) => (
                  <option key={u.userId} value={u.userId}>
                    {u.client} ({u.email})
                  </option>
                ))}
              </select>
              <input
                type="date"
                className="border border-gray-300 rounded-md px-2 py-1"
                value={exportStartDate}
                onChange={(e) => setExportStartDate(e.target.value)}
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                className="border border-gray-300 rounded-md px-2 py-1"
                value={exportEndDate}
                onChange={(e) => setExportEndDate(e.target.value)}
              />
              <button
                onClick={handleDownloadAllocations}
                disabled={downloading || !distribution.length}
                className="bg-[#FF5722] text-white px-3 py-1 rounded-md hover:bg-[#FF5722]/90 transition-colors disabled:opacity-50"
              >
                {downloading ? 'Downloading...' : 'Download'}
              </button>
            </div>
          </div>
          {distribution.length === 0 ? (
            <p className="text-sm text-gray-500">No users found.</p>
          ) : (
            <>
              <div className="space-y-4">
                {distribution
                  .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                  .map((r, index) => {
                    const used = r.used;
                    const allocated = r.allocated || 0;
                    const pct = allocated > 0 ? (used / allocated) * 100 : 0;
                    return (
                      <div key={`${r.userId}-${index}`} className="p-3 bg-gray-50 rounded">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <p className="font-medium">{r.client}</p>
                            <p className="text-sm text-gray-600">
                              {used.toLocaleString()} / {allocated.toLocaleString()} credits used
                            </p>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="font-medium text-[#FF5722]">{pct.toFixed(1)}%</p>
                              <p className="text-sm text-gray-600">Usage</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => openEdit(r)}
                                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteUser(r)}
                                className="px-3 py-1 text-sm border border-red-300 text-red-700 rounded hover:bg-red-50"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full">
                          <div
                            className="h-2 bg-[#FF5722] rounded-full"
                            style={{ width: `${pct}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
              </div>
              {/* Pagination controls */}
              <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
                <div>
                  {(() => {
                    const total = distribution.length;
                    const start = (currentPage - 1) * pageSize + 1;
                    const end = Math.min(currentPage * pageSize, total);
                    return (
                      <span>
                        Showing {start} to {end} of {total} users
                      </span>
                    );
                  })()}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  <span className="px-2">
                    Page {currentPage} of {Math.max(1, Math.ceil(distribution.length / pageSize))}
                  </span>
                  <button
                    className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50"
                    onClick={() =>
                      setCurrentPage((p) =>
                        Math.min(Math.ceil(distribution.length / pageSize) || 1, p + 1)
                      )
                    }
                    disabled={currentPage >= Math.ceil(distribution.length / pageSize)}
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Section 4: Credits Consumed (Used) Export */}
      <div className="bg-white rounded-lg shadow-md p-6 mt-6">
        <div className="border rounded-lg p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
            <div>
              <h3 className="font-medium">Credits Consumed</h3>
              <p className="text-xs text-gray-500">
                Download a CSV of credits used by a specific user over a custom date range for reconciliation.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <select
                className="border border-gray-300 rounded-md px-2 py-1"
                value={usedUserId}
                onChange={(e) => setUsedUserId(e.target.value)}
              >
                <option value="">Select user</option>
                {distribution.map((u) => (
                  <option key={u.userId} value={u.userId}>
                    {u.client} ({u.email})
                  </option>
                ))}
              </select>
              <input
                type="date"
                className="border border-gray-300 rounded-md px-2 py-1"
                value={usedStartDate}
                onChange={(e) => setUsedStartDate(e.target.value)}
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                className="border border-gray-300 rounded-md px-2 py-1"
                value={usedEndDate}
                onChange={(e) => setUsedEndDate(e.target.value)}
              />
              <button
                onClick={handleDownloadUsedCredits}
                disabled={downloadingUsed || !distribution.length}
                className="bg-[#FF5722] text-white px-3 py-1 rounded-md hover:bg-[#FF5722]/90 transition-colors disabled:opacity-50"
              >
                {downloadingUsed ? 'Downloading...' : 'Download credits used'}
              </button>
            </div>
          </div>
        </div>
      </div>
      {editingUser ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <h3 className="text-lg font-medium mb-4">Edit Credits - {editingUser.client}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-700 mb-1">Currently Allocated</label>
                <div className="font-medium">{(editingUser.allocated || 0).toLocaleString()}</div>
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-2">New Allocated Amount</label>
                <input
                  type="number"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={newAllocated}
                  min={0}
                  onChange={(e) => setNewAllocated(Number(e.target.value))}
                />
                <p className="mt-1 text-xs text-gray-500">Note: You can increase allocations here. Reductions require admin action elsewhere.</p>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setEditingUser(null)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#FF5722]/90 transition-colors disabled:opacity-50"
                disabled={saving}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default BillingFinancialManagement; 